import { motion, useReducedMotion } from "motion/react";
import type React from "react";
import { EASE, steps } from "@/components/motion/motion";
import { ANALYTICS_EVENTS, useAnalytics } from "@/lib/analytics";
import { cn } from "@/lib/utils";

/** Page-width wrapper shared by every section. */
export function Container({
	children,
	className,
}: {
	children: React.ReactNode;
	className?: string;
}) {
	return (
		<div className={cn("mx-auto w-full max-w-6xl px-5 md:px-10", className)}>
			{children}
		</div>
	);
}

/**
 * "01 / TITLE ········ subtitle", the section heading of the printed resume.
 * On first view the number ticks in, the title is typed behind a caret, the
 * handwritten note is written out and the dotted leader runs to the margin.
 */
export function SectionTitle({
	n,
	title,
	subtitle,
	as = "h2",
	className,
}: {
	n?: string;
	title: string;
	subtitle?: string;
	as?: "h1" | "h2" | "h3";
	className?: string;
}) {
	const Heading = as;
	const reduced = useReducedMotion();
	const glyphs = Math.max(1, title.length);
	const typing = reduced ? 0 : Math.min(0.85, 0.1 + glyphs * 0.035);
	const start = reduced ? 0 : 0.12;
	const typed = { duration: typing, ease: steps(glyphs), delay: start };

	return (
		<motion.div
			className={cn("flex items-baseline gap-3", className)}
			initial="hidden"
			whileInView="visible"
			viewport={{ once: true, margin: "0px 0px -8% 0px" }}
		>
			{n ? (
				<motion.span
					className="font-mono text-sm text-ink-3"
					aria-hidden="true"
					variants={{
						hidden: { opacity: 0, y: 6 },
						visible: {
							opacity: 1,
							y: 0,
							transition: { duration: 0.5, ease: EASE },
						},
					}}
				>
					{n} /
				</motion.span>
			) : null}
			<Heading className="relative font-mono text-[0.95rem] font-semibold uppercase tracking-[0.16em] text-ink md:text-base">
				<motion.span
					className="inline-block"
					variants={{
						hidden: { clipPath: "inset(-10% 100% -10% 0)" },
						visible: { clipPath: "inset(-10% 0% -10% 0)", transition: typed },
					}}
				>
					{title}
				</motion.span>
				<span
					className="pointer-events-none absolute inset-0 overflow-clip"
					aria-hidden="true"
				>
					<motion.span
						className="absolute inset-0"
						variants={{
							hidden: { x: "0%", opacity: 1 },
							visible: {
								x: "100%",
								opacity: 0,
								transition: {
									x: typed,
									opacity: { duration: 0.2, delay: start + typing + 0.7 },
								},
							},
						}}
					>
						<span className="absolute top-[0.12em] bottom-[0.02em] left-[0.15em] w-[0.6ch] bg-ink" />
					</motion.span>
				</span>
			</Heading>
			{subtitle ? (
				<motion.span
					className="text-hand hidden shrink-0 text-xl sm:inline"
					variants={{
						hidden: { clipPath: "inset(-20% 100% -20% 0)" },
						visible: {
							clipPath: "inset(-20% 0% -20% 0)",
							transition: {
								duration: reduced ? 0 : 1,
								ease: [0.45, 0.05, 0.3, 1],
								delay: start + typing + 0.1,
							},
						},
					}}
				>
					{subtitle}
				</motion.span>
			) : null}
			<motion.span
				className="dotted-leader min-w-6 flex-1 origin-left -translate-y-1"
				aria-hidden="true"
				variants={{
					hidden: { scaleX: 0 },
					visible: {
						scaleX: 1,
						transition: { duration: 1.3, ease: EASE, delay: start },
					},
				}}
			/>
		</motion.div>
	);
}

export type KeyValueItem = {
	key: string;
	value: React.ReactNode;
	href?: string;
	external?: boolean;
};

/** Terminal-style `key:  value` list. */
export function KeyValues({
	items,
	className,
	boot,
}: {
	items: KeyValueItem[];
	className?: string;
	/** Seconds: print the rows one by one on first paint, like a terminal booting. */
	boot?: number;
}) {
	const { capture } = useAnalytics();
	const row = (i: number) =>
		boot === undefined
			? {}
			: {
					className: "line-rise stagger",
					style: {
						"--d": `${boot}s`,
						"--i": i,
						"--step": "0.07s",
					} as React.CSSProperties,
				};
	return (
		<dl
			className={cn(
				"grid grid-cols-[6.5rem_1fr] gap-y-1.5 font-mono text-[0.8rem] leading-snug",
				className,
			)}
		>
			{items.map((item, i) => (
				<div key={item.key} className="contents">
					<dt {...row(i)} className={cn("text-ink-3", row(i).className)}>
						{item.key}:
					</dt>
					<dd
						{...row(i)}
						className={cn("min-w-0 break-words text-ink", row(i).className)}
					>
						{item.href ? (
							<a
								href={item.href}
								onClick={() =>
									capture(ANALYTICS_EVENTS.ctaClick, {
										href: item.href,
										placement: "contact_details",
									})
								}
								className="underline decoration-rule-2 underline-offset-4 transition-colors hover:decoration-ink"
								{...(item.external
									? { target: "_blank", rel: "noreferrer" }
									: {})}
							>
								{item.value}
							</a>
						) : (
							item.value
						)}
					</dd>
				</div>
			))}
		</dl>
	);
}

/** Thin ruled frame, the printed card. */
export function RuledCard({
	children,
	className,
	as: Component = "div",
}: {
	children: React.ReactNode;
	className?: string;
	as?: "div" | "article" | "li" | "section";
}) {
	return (
		<Component className={cn("border border-rule-2 bg-paper-2/55", className)}>
			{children}
		</Component>
	);
}

/** Small mono label above a value, underlined with the pastel gradient. */
export function PastelLabel({
	children,
	className,
}: {
	children: React.ReactNode;
	className?: string;
}) {
	return (
		<span
			className={cn(
				"relative block pb-2 font-mono text-[0.72rem] font-medium uppercase tracking-[0.12em] text-ink",
				className,
			)}
		>
			{children}
			<span
				className="pastel-bar absolute right-0 bottom-0 left-0 h-[3px]"
				aria-hidden="true"
			/>
		</span>
	);
}

/** Mono stack line: "React · Node.js · TypeScript". */
export function StackLine({
	children,
	className,
}: {
	children: React.ReactNode;
	className?: string;
}) {
	return (
		<p className={cn("font-mono text-[0.72rem] text-ink-3", className)}>
			{children}
		</p>
	);
}

/** Running head of the printout: "simon_boisset.cv ··· p. 1". */
export function RunningHead({
	left,
	right,
	className,
}: {
	left: React.ReactNode;
	right?: React.ReactNode;
	className?: string;
}) {
	return (
		<div
			className={cn(
				"flex items-end justify-between border-b border-dashed border-rule-2 pb-2 font-mono text-[0.72rem] text-ink-3",
				className,
			)}
		>
			<span>{left}</span>
			{right ? <span>{right}</span> : null}
		</div>
	);
}
